/**
 * usePWAInstall
 *
 * Отслеживает событие beforeinstallprompt и позволяет вызвать
 * нативный диалог установки PWA. Автоматически определяет,
 * установлено ли уже приложение как standalone.
 *
 * Использование:
 *   const { canInstall, install, isInstalled } = usePWAInstall();
 *
 *   canInstall  — true если браузер готов предложить установку
 *   isInstalled — true если уже запущено как standalone PWA
 *   install()   — вызывает нативный install prompt, возвращает
 *                 'accepted' | 'dismissed'
 *
 * Совместимость:
 *   - Chrome / Edge / Samsung Internet on Android ✓ (beforeinstallprompt)
 *   - Safari iOS ≥ 16.4 ✗ (нет beforeinstallprompt, показываем ручную инструкцию)
 *   - Firefox ✗ (нет beforeinstallprompt, скрываем баннер)
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Расширяем тип BeforeInstallPromptEvent, т.к. он ещё не в стандартном lib
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallOutcome = 'accepted' | 'dismissed';

export interface UsePWAInstallResult {
  /** Браузер поймал beforeinstallprompt — можно показывать баннер */
  canInstall: boolean;
  /** Приложение уже запущено как standalone (PWA установлено) */
  isInstalled: boolean;
  /** true на iOS Safari, где beforeinstallprompt недоступен */
  isIOS: boolean;
  /** Вызывает нативный диалог установки */
  install: () => Promise<InstallOutcome | null>;
  /** Скрыть баннер (пользователь отказался) */
  dismiss: () => void;
  /** Баннер уже был отклонён в этой сессии */
  isDismissed: boolean;
}

const DISMISS_KEY = 'fieldsnap_pwa_install_dismissed';

function detectIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPad на iOS 13+ определяется как Mac
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true
  );
}

export function usePWAInstall(): UsePWAInstallResult {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  const [canInstall, setCanInstall]   = useState(false);
  const [isInstalled, setIsInstalled] = useState(isStandalone);
  const [isIOS]                       = useState(detectIOS);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Не показываем баннер снова если пользователь уже отклонял
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  });

  useEffect(() => {
    // Если уже стоит как PWA — ничего не делаем
    if (isInstalled) return;

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Слушаем успешную установку
    const installedHandler = () => {
      setIsInstalled(true);
      setCanInstall(false);
      deferredPrompt.current = null;
    };
    window.addEventListener('appinstalled', installedHandler);

    // Проверяем display-mode (для случая, когда открыли уже установленное PWA)
    const mq = window.matchMedia('(display-mode: standalone)');
    const mqHandler = (e: MediaQueryListEvent) => {
      if (e.matches) setIsInstalled(true);
    };
    mq.addEventListener('change', mqHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      mq.removeEventListener('change', mqHandler);
    };
  }, [isInstalled]);

  const install = useCallback(async (): Promise<InstallOutcome | null> => {
    if (!deferredPrompt.current) return null;

    try {
      await deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
      setCanInstall(false);

      if (outcome === 'accepted') {
        setIsInstalled(true);
      }

      return outcome;
    } catch (err) {
      console.error('[PWAInstall] prompt() failed:', err);
      return null;
    }
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setIsDismissed(true);
  }, []);

  return { canInstall, isInstalled, isIOS, install, dismiss, isDismissed };
}
